import type { ActivityContext, ActivityHandle } from '../core/activity';
import { mountKeypad } from './keypad';

export type MagicHatOperation = 'plus' | 'minus';

interface RoundConfig {
  maxResultPerRow: number;
  minStars: number[];
  maxStars: number[];
  useDifferentStars?: boolean;
}

interface RawRoundConfig {
  maxResultPerRow?: number;
  minStars?: number[];
  maxStars?: number[];
  useCoefficients?: boolean;
  multiplier?: number;
  rowCoefficients?: number[];
  useDifferentStars?: boolean;
}

interface RawLevel {
  level: number;
  objective: string;
  difficulty: number;
  data: RawRoundConfig[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: RoundConfig[];
}

export function filterStarsLevels(raw: RawLevel[]): Level[] {
  const out: Level[] = [];
  let next = 1;
  for (const lvl of raw) {
    const data = lvl.data.filter((r): r is RoundConfig =>
      !r.useCoefficients && r.maxResultPerRow !== undefined && r.minStars !== undefined && r.maxStars !== undefined,
    );
    if (data.length === 0) continue;
    out.push({ level: next++, objective: lvl.objective, difficulty: lvl.difficulty, data });
  }
  return out;
}

interface MagicHatRunOptions {
  operation: MagicHatOperation;
  title: string;
  activityId: string;
  levels: Level[];
}

const STAR = '⭐';

export function runMagicHatActivity(ctx: ActivityContext, opts: MagicHatRunOptions): ActivityHandle {
  const { operation, title, activityId, levels: LEVELS } = opts;
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let roundIndex = 0;
  let operands: number[] = [];
  let expected = 0;
  let answer = '';
  let correctRoundsCount = 0;
  const opSign = operation === 'plus' ? '+' : '−';

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">${title}</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage magic-hat">
      <div class="feedback" aria-live="polite"></div>
      <p class="magic-hat__progress" aria-live="polite"></p>
      <p class="magic-hat__hint">Quantas estrelas ${operation === 'plus' ? 'no total?' : 'sobraram?'}</p>
      <div class="magic-hat__expression"></div>
      <div class="magic-hat__answer-wrap">
        <div class="magic-hat__answer" aria-live="polite">?</div>
      </div>
      <div class="magic-hat__keypad-wrap"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.magic-hat__progress')!;
  const exprEl = page.querySelector<HTMLElement>('.magic-hat__expression')!;
  const answerEl = page.querySelector<HTMLElement>('.magic-hat__answer')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const keypadWrap = page.querySelector<HTMLElement>('.magic-hat__keypad-wrap')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  mountKeypad(keypadWrap, {
    onDigit: (d) => {
      if (answer.length < 3) answer += d;
      renderAnswer();
    },
    onBackspace: () => {
      answer = answer.slice(0, -1);
      renderAnswer();
    },
    onEnter: validate,
  });

  const onKey = (e: KeyboardEvent): void => {
    if (e.key >= '0' && e.key <= '9' && answer.length < 3) { answer += e.key; renderAnswer(); }
    else if (e.key === 'Backspace') { answer = answer.slice(0, -1); renderAnswer(); }
    else if (e.key === 'Enter') validate();
    else return;
    e.preventDefault();
  };
  window.addEventListener('keydown', onKey);

  function startRound(): void {
    const cfg = level.data[roundIndex]!;
    operands = generateOperands(cfg, operation);
    if (operation === 'plus') expected = operands.reduce((a, b) => a + b, 0);
    else expected = operands.reduce((a, b, i) => i === 0 ? a + b : a - b, 0);
    answer = '';
    renderProgress();
    renderExpression();
    renderAnswer();
  }

  function renderProgress(): void {
    progressEl.textContent = `Rodada ${roundIndex + 1} de ${level.data.length}`;
  }

  function renderExpression(): void {
    exprEl.innerHTML = '';
    operands.forEach((n, i) => {
      const row = document.createElement('div');
      row.className = 'magic-hat__row';
      if (i > 0) {
        const sign = document.createElement('span');
        sign.className = 'magic-hat__sign';
        sign.textContent = opSign;
        row.appendChild(sign);
      } else {
        const spacer = document.createElement('span');
        spacer.className = 'magic-hat__sign magic-hat__sign--empty';
        row.appendChild(spacer);
      }
      const stars = document.createElement('span');
      stars.className = 'magic-hat__stars';
      stars.textContent = n === 0 ? '∅' : STAR.repeat(n);
      stars.dataset.count = String(n);
      row.appendChild(stars);
      const count = document.createElement('span');
      count.className = 'magic-hat__count';
      count.textContent = `(${n})`;
      row.appendChild(count);
      exprEl.appendChild(row);
    });
  }

  function renderAnswer(): void {
    answerEl.textContent = answer === '' ? '?' : answer;
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (answer === '') return;
    const value = parseInt(answer, 10);
    if (value === expected) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctRoundsCount++;
      roundIndex++;
      window.setTimeout(() => {
        if (roundIndex < level.data.length) startRound();
        else finishLevel();
      }, 700);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      answer = '';
      window.setTimeout(renderAnswer, 600);
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    const totalRounds = level.data.length;
    ctx.onComplete(level.level, correctRoundsCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctRoundsCount} de ${totalRounds} ${totalRounds === 1 ? 'rodada' : 'rodadas'}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/${activityId}/${level.level + 1}`;
      });
    }
  }

  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function generateOperands(cfg: RoundConfig, operation: MagicHatOperation): number[] {
  const rows = cfg.minStars.length;
  const max = cfg.maxResultPerRow;
  for (let attempt = 0; attempt < 50; attempt++) {
    const vals: number[] = [];
    for (let i = 0; i < rows; i++) {
      vals.push(randInt(cfg.minStars[i] ?? 0, cfg.maxStars[i] ?? 0));
    }
    const nonZero = vals.filter((v) => v > 0);
    if (nonZero.length === 0) continue;
    if (operation === 'plus') {
      const sum = vals.reduce((a, b) => a + b, 0);
      if (sum <= max && sum > 0) return nonZero;
    } else {
      const first = vals[0]!;
      const rest = vals.slice(1).reduce((a, b) => a + b, 0);
      if (first >= rest && first <= max && first > 0) {
        const opsFiltered: number[] = [first];
        for (let i = 1; i < vals.length; i++) {
          if (vals[i]! > 0) opsFiltered.push(vals[i]!);
        }
        return opsFiltered.length >= 2 ? opsFiltered : [first, 0];
      }
    }
  }
  if (operation === 'plus') return [1, 1];
  return [2, 1];
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
